import moment from "moment";
import PropTypes from "prop-types";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useBodyweightGoalForm } from "../../../../hooks/goals/useBodyweightGoalForm";
import { getBodywweightAndUnit } from "../../../../utils/converters/convert-units";
import {
  resolveActiveBodyweightGoal,
  toUtcNumber,
} from "../../../../utils/nutrition-progress";
import SvgIcon from "../../../SvgIcon";
import { getUnitsOptions } from "../../profile/AddGoal";
import { BodyweightFields } from "../../profile/BodyweightFields";

// Bodyweight section of Periodization's manage-milestone-goals modal (see
// ~/.claude/plans/periodization-add-goal-modal.md Fase 4). Unlike Personal
// Record (Fase 5/6), there's only ever one active bodyweight goal at a time -
// no list to declutter, so no hiddenFromPlanner checkbox here (plan section
// 2, decision 4). `resolveActiveBodyweightGoal` is the exact same "goal
// currently in force" rule PhaseSummary/Nutrition already use, so this
// section never disagrees with those about which goal is "the" one.
export const BodyweightGoalSection = ({
  clientGoals,
  units,
  query,
  assert,
  flash,
}) => {
  const { t } = useTranslation(["athlete-portal", "common"]);
  const todayUtc = toUtcNumber(new Date());
  const activeGoal = resolveActiveBodyweightGoal(clientGoals, todayUtc);
  const unitsOptions = getUnitsOptions(t);

  const [isEditing, setIsEditing] = useState(false);

  // State/validation/submit shared with AddGoal.js's own bodyWeight branch -
  // see the hook's own comment for why. Stays open on save (unlike
  // AddGoal.js) via save-milestone-goal (see client-goals.js).
  const {
    targetWeight,
    setTargetWeight,
    setTargetUnits,
    targetDate,
    setTargetDate,
    isUnchanged,
    submitBodyweightGoal,
  } = useBodyweightGoalForm({
    goal: activeGoal,
    units,
    assert,
    flash,
    buildUrl: (id) =>
      id
        ? `/actions/client-goals/save-milestone-goal/${id}?proxyAthlete=${query.athlete}`
        : `/actions/client-goals/save-milestone-goal?proxyAthlete=${query.athlete}`,
  });
  const isSaveDisabled = !targetWeight || isUnchanged;

  return (
    <div className="goal-section bodyweight-goal-section">
      <h3 className="goal-section-title">
        <SvgIcon src="/img/new-icons/bullseye.svg" />
        {t("annualPlanner.goalsSectionTitle")}
      </h3>

      {isEditing && (
        <div className="inline-goal-form">
          <BodyweightFields
            t={t}
            unitsOptions={unitsOptions}
            units={units}
            targetWeight={targetWeight}
            setTargetWeight={setTargetWeight}
            setTargetUnits={setTargetUnits}
            targetDate={targetDate}
            setTargetDate={setTargetDate}
          />
          <div className="inline-form-actions">
            <button
              type="button"
              data-button-type="secondary"
              onClick={() => setIsEditing(false)}
            >
              {t("common:cancel")}
            </button>
            <button
              type="button"
              data-button-type="primary"
              disabled={isSaveDisabled}
              onClick={() => {
                submitBodyweightGoal();
                setIsEditing(false);
              }}
            >
              {t("common:save")}
            </button>
          </div>
        </div>
      )}

      {!isEditing && activeGoal && (
        <div className="goal-row">
          <div className="goal-row-info">
            <span className="goal-name">
              {t("coaching-portal:addGoal.bodyweight")}
            </span>
            <span className="goal-detail">
              {getBodywweightAndUnit(activeGoal.details.targetWeight, units)}
            </span>
          </div>
          <span className="goal-date">
            {moment(String(activeGoal.utcTargetDate), "YYYYMMDD").format(
              "D MMM, YYYY"
            )}
          </span>
          <button
            type="button"
            className="goal-edit-pencil"
            onClick={() => setIsEditing(true)}
          >
            <SvgIcon src="/img/new-icons/edit-pencil.svg" />
          </button>
        </div>
      )}

      {!isEditing && !activeGoal && (
        <button
          type="button"
          className="dashed-box add-goal-ghost-row"
          onClick={() => setIsEditing(true)}
        >
          <span className="add-goal-ghost-label">
            + {t("annualPlanner.addBodyweightGoal")}
          </span>
        </button>
      )}
    </div>
  );
};

BodyweightGoalSection.propTypes = {
  clientGoals: PropTypes.array,
  units: PropTypes.string,
  query: PropTypes.object,
  assert: PropTypes.func.isRequired,
  flash: PropTypes.func.isRequired,
};
